/**
 * Application entry point.
 */
import { createRoot } from 'react-dom/client';
import App from './App';
import '@/app.css';
import '@/styles/global.css';

// Глобальный перехватчик fetch для локальной раздачи моделей Kokoro без VPN
const originalFetch = window.fetch;
window.fetch = async function (input, init) {
  let url = '';
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof URL) {
    url = input.href;
  } else if (input && typeof input === 'object' && 'url' in input) {
    url = (input as any).url;
  }

  if (url) {
    // Поддерживаем как старое название (с v1.0), так и новое стандартное
    const match = url.match(/\/(onnx-community\/Kokoro-82M(?:-v1\.0)?-ONNX)\/(?:resolve\/main\/|raw\/main\/)?(.*)/);
    if (match) {
      const modelDir = match[1]; // e.g. onnx-community/Kokoro-82M-ONNX
      let filePart = match[2];    // e.g. config.json, tokenizer.json, onnx/model_quantized.onnx, voices/af_heart.bin
      
      if (filePart) {
        filePart = filePart.split('?')[0]?.split('#')[0] || '';
      }

      const localUrl = `/models/${modelDir}/${filePart}`;
      try {
        const response = await originalFetch(localUrl, init);
        if (response.ok) {
          return response;
        }
        // Если конкретный .onnx файл не найден локально, подставляем URL с сервера
        if (filePart && filePart.includes('.onnx')) {
          const cdnResponse = await originalFetch('https://ep-test.langplat.com/models/onnx-community/Kokoro-82M-ONNX/onnx/model_quantized.onnx', init);
          if (cdnResponse.ok) return cdnResponse;
        }
      } catch (err) {
        // Игнорируем ошибку
      }
    }
    
    // Также ловим перенаправления на CDN-хосты Hugging Face для любых .onnx моделей и голосов
    if (url.includes('aws.cdn.hf.co') || url.includes('xet-bridge-us') || url.includes('huggingface.co')) {
      let filePart = '';
      if (url.includes('.onnx')) {
        filePart = 'onnx/model_quantized.onnx';
      } else {
        const voiceMatch = url.match(/([a-z]{2}_[a-z]+\.bin)/);
        if (voiceMatch) {
          filePart = `voices/${voiceMatch[1]}`;
        }
      }

      if (filePart) {
        const localUrl = `/models/onnx-community/Kokoro-82M-ONNX/${filePart}`;
        try {
          const response = await originalFetch(localUrl, init);
          if (response.ok) {
            return response;
          }
        } catch (err) {
          // Игнорируем ошибку
        }
      }
    }
  }
  return originalFetch(input, init);
};

// Register Service Worker for Web Push notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    if (import.meta.env.DEV) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister().then((success) => {
            if (success) {
              console.log('[SW] Unregistered active Service Worker to apply updates.');
              window.location.reload();
            }
          });
        }
      });
    } else {
      navigator.serviceWorker.register('/sw.js').catch((err) =>
        console.warn('[SW] Registration failed:', err)
      );
    }
  });
}

// Auto-recover from dynamic import 404 errors caused by new deployments (Vite chunk hash changes)
const handleChunkError = (msg: string) => {
  if (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg)
  ) {
    const storageKey = 'ep_chunk_reload_' + window.location.pathname;
    const lastReload = sessionStorage.getItem(storageKey);
    const now = Date.now();
    if (!lastReload || now - Number(lastReload) > 10000) {
      sessionStorage.setItem(storageKey, String(now));
      window.location.reload();
    }
  }
};

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  handleChunkError('Failed to fetch dynamically imported module');
});

window.addEventListener('error', (event) => {
  handleChunkError(event?.message || event?.error?.message || '');
}, true);

window.addEventListener('unhandledrejection', (event) => {
  const reason = event?.reason;
  handleChunkError(typeof reason === 'string' ? reason : reason?.message || '');
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <App />,
);

